import { CARBON_FOOTPRINT_DATA } from '../constants/AppConstants';

// Calculates the distance between two points (with latitude and longitude in degrees) on the Earth's surface
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3981.875; // Radius of the Earth in miles

    // Convert latitude and longitude from degrees to radians
    const toRadians = (degrees) => degrees * Math.PI / 180;
    const phi1 = toRadians(lat1);
    const phi2 = toRadians(lat2);

    // Calculate the difference in latitude and longitude in radians
    const deltaPhi = toRadians(lat2 - lat1);
    const deltaLambda = toRadians(lon2 - lon1);

    // Calculate the square of half the chord length between the points using the Haversine formula
    const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
        Math.cos(phi1) * Math.cos(phi2) *
        Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);

    // Calculate the angular distance in radians using the Haversine formula
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    // Return the distance by multiplying the angular distance by the Earth's radius
    return R * c;
};

const getOriginAndDestinationFromUnknownFare = (fare, origin, destination) => {
    if (!origin || !destination) {
        if (fare.outwardJourney) {
            return {
                origin: fare.outwardJourney.getOrigin(),
                destination: fare.outwardJourney.getDestination(),
            };
        } else {
            return {
                origin: fare.origin,
                destination: fare.destination
            };
        }
    }
    return {
        origin,
        destination
    };
};

export const calculateCarbonEmissions = ({ fare, originStation, destinationStation }) => {
    const { origin, destination } = getOriginAndDestinationFromUnknownFare(fare, originStation, destinationStation);

    const { lat: lat1, lng: lng1 } = origin.getLatLng();
    const { lat: lat2, lng: lng2 } = destination.getLatLng();

    const hasRequiredData = Boolean(lat1) && Boolean(lng1) && Boolean(lat2) && Boolean(lng2);

    if ( !hasRequiredData ) {
        return { requiredDataNotProvided: true };
    }

    const passengersAmount = fare.adults() + fare.children();
    const ticketsAmount = fare.ticketType.isReturn ? 2 : 1;

    const dist = calculateDistance(lat1, lng1, lat2, lng2);

    // calc journey CO2 emissions
    const carEmissions = dist * passengersAmount * ticketsAmount * CARBON_FOOTPRINT_DATA.CAR_EMISSIONS_PER_MILE;
    const trainEmissions = dist * passengersAmount * ticketsAmount * CARBON_FOOTPRINT_DATA.TRAIN_EMISSIONS_PER_MILE;

    // calc difference in CO2 emissions
    const emissionsDifference = carEmissions - trainEmissions;

    // make sure the train percentage is between 15 and 30, so the car percentage is between around 45 and 90
    const trainPercentage = Math.min(Math.max(trainEmissions * 1.5, 15), 30);
    const carPercentage = (trainPercentage * CARBON_FOOTPRINT_DATA.CAR_EMISSIONS_PER_MILE) / CARBON_FOOTPRINT_DATA.TRAIN_EMISSIONS_PER_MILE;

    return {
        trainEmissions: trainEmissions.toFixed(2),
        carEmissions: carEmissions.toFixed(2),
        trainPercentage: Math.round(trainPercentage),
        carPercentage: Math.round(carPercentage),
        distance: dist.toFixed(2),
        origin: origin.getName(),
        destination: destination.getName(),
        savedCO2e: emissionsDifference.toFixed(2),
        bulb: (emissionsDifference / CARBON_FOOTPRINT_DATA.BULB_PER_KG).toFixed(2),
        fullyChargingSmartphones: (emissionsDifference / CARBON_FOOTPRINT_DATA.PHONE_PER_KG).toFixed(2),
        kettle: (emissionsDifference / CARBON_FOOTPRINT_DATA.KETTLE_PER_KG).toFixed(2),
        microwave: (emissionsDifference / CARBON_FOOTPRINT_DATA.MICROWAVE_PER_KG).toFixed(2),
        tv: (emissionsDifference / CARBON_FOOTPRINT_DATA.TV_PER_KG).toFixed(2),
        washingMachine: (emissionsDifference / CARBON_FOOTPRINT_DATA.WASHING_MACHINE_PER_KG).toFixed(2),
    };
};



// WEBPACK FOOTER //
// ./src/utils/carbonEmissionsUtils.js